import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
// AUDIT-21: refresh_token httpOnly cookie'sinin Set-Cookie header'ını tarayıcının
// kabul etmesi için login/sso/refresh çağrılarında withCredentials açık olmalı.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Clear stale cached user data that has empty role (from old migration)
    const cachedUser = localStorage.getItem('restaflex_user');
    if (cachedUser) {
      try {
        const parsed = JSON.parse(cachedUser);
        if (!parsed.role) {
          localStorage.removeItem('restaflex_user');
          localStorage.removeItem('restaflex_token');
        }
      } catch { /* invalid JSON — clear it */ localStorage.removeItem('restaflex_user'); }
    }

    const init = async () => {
      // Check for branch_token (impersonation, tab-isolated) or sso_code (SSO login)
      const params = new URLSearchParams(window.location.search);
      const branchToken = params.get('branch_token');
      const ssoCode = params.get('sso_code');

      if (branchToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${branchToken}`;
        try {
          const res = await axios.get(`${API_BASE}/auth/me`);
          if (res.data.success) {
            setToken(branchToken);
            setUser(res.data.user);
            // Branch tokens are tab-scoped — use sessionStorage to avoid overwriting parent tab's chain token
            sessionStorage.setItem('restaflex_branch_token', branchToken);
            sessionStorage.setItem('restaflex_branch_user', JSON.stringify(res.data.user));
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch {
          delete axios.defaults.headers.common['Authorization'];
        }
        setLoading(false);
        return;
      }

      if (ssoCode) {
        // Exchange single-use SSO code for JWT — JWT never appears in the URL
        try {
          const res = await axios.post(`${API_BASE}/auth/sso-exchange`, { code: ssoCode });
          if (res.data.success) {
            const { token: newToken, user: newUser } = res.data;
            setToken(newToken);
            setUser(newUser);
            localStorage.setItem('restaflex_token', newToken);
            localStorage.setItem('restaflex_user', JSON.stringify(newUser));
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          }
        } catch { /* invalid/expired code — fall through to normal login */ }
        window.history.replaceState({}, '', window.location.pathname);
        setLoading(false);
        return;
      }

      // Check for tab-scoped branch session
      const branchSessionToken = sessionStorage.getItem('restaflex_branch_token');
      const branchSessionUser = sessionStorage.getItem('restaflex_branch_user');
      if (branchSessionToken && branchSessionUser) {
        setToken(branchSessionToken);
        setUser(JSON.parse(branchSessionUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${branchSessionToken}`;
        setLoading(false);
        return;
      }

      // Normal localStorage auth — verify with server before setting user
      let storedToken = localStorage.getItem('restaflex_token');
      if (storedToken) {
        // AUDIT-21: Önce sessizce refresh dene — eğer httpOnly cookie hâlâ geçerliyse
        // taze 15-dk access token al. Cookie yoksa eski token ile devam et (back-compat).
        try {
          const refreshRes = await axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
          if (refreshRes.data?.success && refreshRes.data?.token) {
            storedToken = refreshRes.data.token;
            localStorage.setItem('restaflex_token', storedToken);
            if (refreshRes.data.user) {
              localStorage.setItem('restaflex_user', JSON.stringify(refreshRes.data.user));
            }
          }
        } catch { /* refresh başarısız → eski token ile devam, /me 401 dönerse logout */ }

        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        try {
          const res = await axios.get(`${API_BASE}/auth/me`);
          if (res.data.success) {
            setToken(storedToken);
            setUser(res.data.user);
            localStorage.setItem('restaflex_user', JSON.stringify(res.data.user));
          } else {
            logout();
          }
        } catch (err) {
          if (err.response?.status === 401) {
            // Refresh de denedik, /me de 401 → tam çıkış
            logout();
          } else {
            // Server unreachable — use cached user to avoid logout on network hiccup
            const storedUser = localStorage.getItem('restaflex_user');
            if (storedUser) {
              setToken(storedToken);
              setUser(JSON.parse(storedUser));
            } else {
              logout();
            }
          }
        }
      }
      setLoading(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IMP-02: 2FA tamamlama — pre_token + code → tam JWT
  const completeLogin2FA = async (pre_token, code) => {
    const res = await axios.post(`${API_BASE}/auth/2fa/login`, { pre_token, code });
    if (res.data.success && res.data.token) {
      const newToken = res.data.token;
      const newUser = res.data.user;
      setToken(newToken);
      setUser(newUser);
      localStorage.setItem('restaflex_token', newToken);
      localStorage.setItem('restaflex_user', JSON.stringify(newUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return newUser;
    }
    throw new Error('2FA doğrulanamadı');
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API_BASE}/auth/login`, { email, password });
    // IMP-02: TOTP gerekiyorsa pre_token döndür, çağıran 2. adımı yönetir
    if (res.data.totp_required) {
      return { totp_required: true, pre_token: res.data.pre_token };
    }
    if (res.data.success) {
      const newToken = res.data.token;
      const newUser = res.data.user;
      setToken(newToken);
      setUser(newUser);
      localStorage.setItem('restaflex_token', newToken);
      localStorage.setItem('restaflex_user', JSON.stringify(newUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      // After successful login, fetch and store the default page setting
      if (newUser.role === 'admin') {
        try {
          const settingsRes = await axios.get(`${API_BASE}/settings`, {
            headers: { Authorization: `Bearer ${newToken}` },
          });
          const dp = settingsRes.data?.data?.settings?.default_page;
          if (dp) localStorage.setItem('restaflex_default_page', dp);
        } catch {}
      }

      return newUser;
    }
    throw new Error(res.data.message || 'Giriş başarısız');
  };

  const logout = () => {
    // AUDIT-21: Backend'e logout bildirip refresh cookie'yi temizle (fire-and-forget)
    try {
      axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    } catch { /* ignore */ }
    setToken(null);
    setUser(null);
    localStorage.removeItem('restaflex_token');
    localStorage.removeItem('restaflex_user');
    sessionStorage.removeItem('restaflex_branch_token');
    sessionStorage.removeItem('restaflex_branch_user');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, completeLogin2FA }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
